/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { PageHeader } from '../../company-profile/profile.styled';
import Heading from '../../../../components/typography';
import { COLORS } from '../../../../constants/style/color';
import { type TableParamsType } from '../../../../types/common';
import InitialValuesForm from '../../../../constants/initialValues';

import {
  useBoolean,
  useGetTaxItemListFromWealthlane,
  useGetTaxItemViewForWealthlane,
  useGetTaxJuridictionLevel,
  useGetTaxPeriodAll,
  useGetTaxPeriodVendor,
} from '../../../../hooks';
import FormSelect from '../../../../components/dropdown/formSelect';
import useWealthlaneTaxApi from '../../../../hooks/useWealthlaneTaxApi';
import { CopyTaxDataBlock, LeftSideBar, LibraryContainer, RightSideBar } from './taxLibrary.styled';
import Search from '../../../../components/search/search';
import { ResetFilterWrapper } from '../../../../constants/style/common.styled';
import Filter from '../../../../components/filter';
import { RadioButtonWrapper } from '../../../../components/radio-button/radioButton.styled';
import { TableBodyCellColumn } from '../../../../constants/style/table.styled';
import Button from '../../../../components/button';
import {
  TaxItemService,
  Wealthlane_TaxService_Dtos_Response_ResponseDto,
  Wealthlane_TaxService_Dtos_TaxItemDto_VendorTaxItemIdsListDto,
} from '../../../../services/wealthlane-tax-services';
import NoDataFound from '../../../../components/no-data-found';
import { toast } from 'react-toastify';
import ToastComponent from '../../../../components/toast';
import { useQueryClient } from 'react-query';
import { isEmpty } from 'lodash';
import Spinners from '../../../../components/spinner';
import AlertPopup from '../../../../components/alert-popup';
import TaxLibraryView from '../tax-library/taxLibraryView';
import moment from 'moment';
import { DATE_FORMAT } from '../../../../constants/common';
const TaxLibrary = (): JSX.Element => {
  const { handleRequest, error } = useWealthlaneTaxApi();

  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [tableInfo, setTableInfo] = useState<TableParamsType>(InitialValuesForm.tableParams);
  const [viewItemId, setViewItemId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [checkedValues, setCheckedValues] = useState<string[]>([]);
  let checked = false;
  const { data: taxPeriodVendorOptions, isLoading: isTaxPeriodVendorOptionsLoading } =
    useGetTaxPeriodVendor(handleRequest);
  const { data: juridictionLevelOptions, isLoading: isJuridictionLevelOptionsLoading } =
    useGetTaxJuridictionLevel(handleRequest);
  const { data: taxViewItemDetails, isLoading: viewLoader } = useGetTaxItemViewForWealthlane(
    handleRequest,
    viewItemId
  );
  const { data: taxItemFromWealthlaneList, isLoading: listLoader } =
    useGetTaxItemListFromWealthlane(handleRequest, tableInfo);
  const [copyItemList, setCopyItemList] = useState<
    Wealthlane_TaxService_Dtos_TaxItemDto_VendorTaxItemIdsListDto[] | undefined | null
  >(null);
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isToastOpen,
    setFalse: closeToastModal,
    setTrue: openToastModal,
  } = useBoolean(false);

  useEffect(() => {
    if (taxItemFromWealthlaneList !== undefined) {
      setCopyItemList(taxItemFromWealthlaneList);
    }
  }, [taxItemFromWealthlaneList]);

  useEffect(() => {
    if (searchQuery === '') {
      setCopyItemList(taxItemFromWealthlaneList);
    }
  }, [searchQuery]);

  useEffect(() => {
    setCopyItemList(null);
  }, [tableInfo?.jurisdictionLevelId, tableInfo?.vendorTaxPeriodId]);

  if (
    copyItemList !== undefined &&
    copyItemList !== null &&
    checkedValues.length === copyItemList?.length &&
    checkedValues.length !== 0
  ) {
    checked = true;
  }

  const selectedTaxItem = taxItemFromWealthlaneList?.find((item) => item.id === viewItemId);
  const selectedItemNameAlreadyExists = selectedTaxItem?.nameAlreadyExists === true;

  const handleSearch = (value: string): void => {
    const searchedData = taxItemFromWealthlaneList?.filter((item: any) =>
      item.name.toLowerCase().includes(value.toLowerCase())
    );
    setCopyItemList(searchedData);
  };
  const handleSubmit = () => {
    setLoading(true);
    handleRequest(TaxItemService.postApiTaxApiAppTaxItemVendorTaxCopyByCompany(checkedValues))
      .then((response: Wealthlane_TaxService_Dtos_Response_ResponseDto | any) => {
        if (response) {
          openToastModal();
          setMessage(response?.message);
          closeModal();
          openToastModal();
          setCheckedValues([]);
          setViewItemId('');
          setLoading(false);
        }
      })
      .catch((err) => {
        setLoading(false);
        closeModal();
        openToastModal();
      })
      .finally(() => {
        setLoading(false);
        closeModal();
        openToastModal();
      });
  };

  const handleCheckAll = (checked: boolean): void => {
    const keys: any = checked
      ? !isEmpty(checkedValues)
        ? []
        : copyItemList?.map((items: any) => items.id)
      : [];

    setCheckedValues(keys);
  };

  const handleCheck = (id: string) => {
    const keys: any = !checkedValues.includes(id)
      ? [...checkedValues, id]
      : checkedValues.filter((item) => item !== id);

    setCheckedValues(keys);
  };

  return (
    <div id="taxLibraryPageContainer">
      <PageHeader id="taxLibraryPageHeader">
        <Heading variant='h3' title={`Tax Library`} id="taxLibraryPageTitle" />
        <Heading
          variant='body-s'
          title={`View all tax items in wealthlane company.`}
          color={`${COLORS.Gray}`}
          id="taxLibraryPageDescription"
        />
      </PageHeader>
      <LibraryContainer id="taxLibraryContainer">
        <LeftSideBar className='flex flex-col justify-between gap-y-5' id="taxLibraryLeftSidebar">
          <div id="taxLibraryLeftSidebarContent">
            <div className='flex flex-row gap-x-5' id="taxLibrarySearchFilterContainer">
              <Search
                placeholder='Search'
                searchValue={searchQuery}
                onSearch={(value: string) => {
                  setSearchQuery(value);
                  handleSearch(value);
                  setCheckedValues([]);
                }}
                setTableInfo={setTableInfo}
                tableInfo={tableInfo}
                id="taxLibrarySearch"
              />

              <Filter setOpen={setOpen} open={open} className='p-0 m-0' id="taxLibraryFilter">
                <div className='flex justify-between' id="taxLibraryFilterHeader">
                  <Heading variant='body-xs' title='Filter By:' className='filter-label mb-0' id="taxLibraryFilterTitle" />
                  <div className='mb-4 lg:mb-2' id="taxLibraryResetFilterContainer">
                    <ResetFilterWrapper
                      onClick={() => {
                        setTableInfo({
                          ...InitialValuesForm.tableParams,
                          taxPeriodId: tableInfo.taxPeriodId,
                          filter: '',
                        });
                        setSearchQuery('');
                        setCheckedValues([]);
                        setOpen(false);
                        checked = false;
                      }}
                      id="taxLibraryResetFilterButton">
                      <span className='material-symbols-outlined' id="taxLibraryResetFilterIcon">settings_backup_restore</span>
                      <Heading variant='body-s' title='Reset Filter' id="taxLibraryResetFilterText" />
                    </ResetFilterWrapper>
                  </div>
                </div>
                <div className='grid md:grid-cols-2 grid-cols-1 gap-x-5' id="taxLibraryFilterFields">
                  <FormSelect
                    type='text'
                    name='taxPeriod'
                    id='taxPeriod'
                    placeholder='Tax Period'
                    onChange={(value: string) => {
                      setTableInfo({ ...tableInfo, vendorTaxPeriodId: value });
                    }}
                    options={taxPeriodVendorOptions}
                    isLoading={isTaxPeriodVendorOptionsLoading}
                    values={tableInfo.vendorTaxPeriodId}
                    arrValue={tableInfo.vendorTaxPeriodId}
                    isFormik={false}
                  />
                  <FormSelect
                    type='text'
                    name='jurisdictionLevel'
                    id='jurisdictionLevel'
                    placeholder='Jurisdiction Level'
                    onChange={(value: string) => {
                      setTableInfo({ ...tableInfo, jurisdictionLevelId: value });
                    }}
                    options={juridictionLevelOptions}
                    isLoading={isJuridictionLevelOptionsLoading}
                    values={tableInfo.jurisdictionLevelId}
                    arrValue={tableInfo.jurisdictionLevelId}
                    isFormik={false}
                  />
                </div>
              </Filter>
            </div>
            <div className='tax-item' id="taxLibraryTaxItemContainer">
              {listLoader ? (
                <Spinners containerClassName='h-[550px]' id="taxLibraryLoadingSpinner" />
              ) : copyItemList !== undefined &&
                copyItemList !== null &&
                copyItemList?.length > 0 ? (
                <div
                  className={`tax-library-radio-wrapper-select-all pl-0 border-b-2 `}
                  key={'Select All'}
                  id="taxLibrarySelectAllContainer">
                  <RadioButtonWrapper
                    label={'Select All'}
                    className={`${copyItemList !== undefined &&
                        copyItemList !== null &&
                        checkedValues.length < copyItemList?.length &&
                        checkedValues.length > 0
                        ? 'checkboxWrapper block partialSelect'
                        : 'checkboxWrapper block'
                      }`}
                    id={'Select All'}>
                    <input
                      type='checkbox'
                      id={'taxLibrarySelectAllCheckbox'}
                      checked={checked}
                      onChange={(event: any) => {
                        handleCheckAll(event.target.checked);
                      }}
                    />
                  </RadioButtonWrapper>
                  <label htmlFor={'taxLibrarySelectAllCheckbox'} className='tax-library-radio--label' id="taxLibrarySelectAllLabel">
                    <TableBodyCellColumn id="taxLibrarySelectAllCell">
                      <div className='cellTitle' id="taxLibrarySelectAllTitle">{'Select All'}</div>
                    </TableBodyCellColumn>
                  </label>
                </div>
              ) : (
                <div className='flex justify-center items-center h-full' id="taxLibraryNoDataContainer">
                  <NoDataFound message='No Data Found' />
                </div>
              )}
              <CopyTaxDataBlock
                style={{
                  height: checkedValues?.length >= 1 ? '510px' : '550px',
                }}
                id="taxLibraryCopyTaxDataBlock">
                {copyItemList !== undefined &&
                  (copyItemList?.map((item: any, index: number) => {
                    return (
                      <div
                        className={`tax-library-radio-wrapper mt-3 ${viewItemId === item?.id ? 'active' : ''
                          }`}
                        key={index}
                        id={`taxLibraryTaxItem.${index}`}>
                        <RadioButtonWrapper
                          label={item?.name}
                          className='checkboxWrapper'
                          id={item?.id}
                          onClick={() => handleCheck(item?.id)}>
                          <input
                            type='checkbox'
                            id={`taxLibraryTaxItemCheckbox.${index}`}
                            className='z-10'
                            checked={checkedValues.some((item2: any) => item2 === item.id)}
                          />
                        </RadioButtonWrapper>
                        <label
                          htmlFor={`taxLibraryTaxItemCheckbox.${index}`}
                          className='tax-library-radio--label'
                          onClick={() => {
                            setViewItemId(item?.id);
                          }}
                          id={`taxLibraryTaxItemLabel.${index}`}>
                          <TableBodyCellColumn id={`taxLibraryTaxItemCell.${index}`}>
                            <div
                              className='cellTitle'
                              style={{
                                color: `${item?.nameAlreadyExists ? COLORS.Iris : COLORS.Black}`,
                              }}
                              id={`taxLibraryTaxItemTitle.${index}`}>
                              {item?.name}
                            </div>
                            <div className='cellSubtitle' style={{ color: COLORS.Black }} id={`taxLibraryTaxItemDateRange.${index}`}>
                              {moment(item?.effectiveFromDate).format(DATE_FORMAT)} -{' '}
                              {moment(item?.effectiveToDate).format(DATE_FORMAT)}
                            </div>
                            <div className='cellSubtitle' id={`taxLibraryTaxItemJurisdiction.${index}`}>{item?.jurisdictionName}</div>
                          </TableBodyCellColumn>
                        </label>
                      </div>
                    );
                  }) as any)}
              </CopyTaxDataBlock>
            </div>
          </div>
          <div className='flex flex-col' id="taxLibraryLeftSidebarFooter">
            {checkedValues?.length > 0 && (
              <div className='flex justify-center copy-tax-button' id="taxLibraryCopyTaxButtonContainer">
                <Button
                  variant={'primary'}
                  title={'Copy Selected Taxes'}
                  icon='add_circle_outline'
                  btnClassName='w-full'
                  id="taxLibraryCopySelectedTaxesButton"
                  onClick={() => {
                    openModal();
                  }}
                  loading={loading}
                />
              </div>
            )}
            <div className='legend-block' id="taxLibraryLegendBlock">
              <Heading variant='body-xs' title='Legend' id="taxLibraryLegendTitle" />
              <div className='flex items-center' id="taxLibraryLegendItem">
                <div className='duplicateIndex' id="taxLibraryLegendIcon" />
                <Heading variant='body-xs' title='Exist in your company' id="taxLibraryLegendText" />
              </div>
            </div>
          </div>
        </LeftSideBar>

        <RightSideBar className='w-full' id="taxLibraryRightSidebar">
          {viewLoader ? (
            <Spinners containerClassName='h-normal-screen' id="taxLibraryViewLoadingSpinner" />
          ) : taxViewItemDetails !== undefined ? (
            <div className='view-block' id="taxLibraryViewBlock">
              <div className='tax-header' id="taxLibraryViewHeader">
                <Heading
                  variant='h4'
                  title={taxViewItemDetails?.name}
                  className='tax-header-title'
                  id="taxLibraryViewTitle"
                />
                {selectedItemNameAlreadyExists && (
                  <Heading
                    className='tax-header-subtitle'
                    variant='body-s'
                    title={`This tax item currently exists in your company. If you decide to copy this tax item from the library to your company, your current tax item in your company will be updated with the details shown below.`}
                    color={`${COLORS.Gray}`}
                    id="taxLibraryViewDescription"
                  />
                )}
              </div>
              <div className='tax-body' id="taxLibraryViewBody">
                <TaxLibraryView viewData={taxViewItemDetails} id="taxLibraryViewComponent" />
                <div className='copy-button' id="taxLibraryViewCopyButtonContainer">
                  <Button
                    variant='primary-outline'
                    title='Copy this tax'
                    id="taxLibraryViewCopyThisTaxButton"
                    onClick={() => {
                      taxViewItemDetails?.id && setCheckedValues([taxViewItemDetails?.id]);
                      openModal();
                    }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className='flex justify-center items-center h-full' id="taxLibraryViewNoDataContainer">
              <NoDataFound message='No Data Found' />
            </div>
          )}
        </RightSideBar>
        {isModalOpen && (
          <AlertPopup
            isShow={isModalOpen}
            handleSubmit={handleSubmit}
            closeModal={closeModal}
            width={'700px'}
            id="taxLibraryCopyConfirmationModal"
            customTitle={
              <div className='confirm' id="taxLibraryCopyConfirmationContent">
                <Heading variant='h5' title='Preview' id="taxLibraryCopyConfirmationTitle" />

                <span className='confirm-title' id="taxLibraryCopyConfirmationSubtitle1">Copying Library Items</span>
                <span className='confirm-subtitle' id="taxLibraryCopyConfirmationDescription1">
                  You have selected {checkedValues?.length > 0 ? checkedValues?.length : '0'} item
                  {checkedValues?.length <= 1 ? '' : 's'} to copy from the library to your company.
                </span>

                <span className='confirm-title' id="taxLibraryCopyConfirmationSubtitle2">Updating Existing Items</span>
                <span className='confirm-subtitle' id="taxLibraryCopyConfirmationDescription2">
                  Note that these copied tax items will apply to the tax period defined by the
                  library. Any existing items in your company having the same name as the library
                  items will be updated.
                </span>

                <span className='confirm-title' id="taxLibraryCopyConfirmationSubtitle3">Confirm and Certify</span>
                <span className='confirm-subtitle' id="taxLibraryCopyConfirmationDescription3">
                  By selecting "Confirm", you are certifying that you have reviewed the items and
                  that they are applicable for your company. After copying them to your company, you
                  can choose to remove them or further modify them according to your needs.
                </span>

                <span className='confirm-title' id="taxLibraryCopyConfirmationSubtitle4">Consult Tax Advisor</span>
                <span className='confirm-subtitle' id="taxLibraryCopyConfirmationDescription4">
                  You understand that these library items are not recommendations for your tax
                  treatment. You should consult with your tax advisor for their suitability.
                </span>
              </div>
            }
            loading={loading}
          />
        )}

        {isToastOpen && (
          <AlertPopup
            isShow={isToastOpen}
            closeModal={() => {
              closeToastModal();
              void queryClient.invalidateQueries('getTaxPeriodTableForWealthlaneList');
            }}
            width={'500px'}
            display={false}
            id="taxLibraryToastModal"
            customTitle={
              <div className='status' id="taxLibraryToastContent">
                <span
                  className='material-symbols-outlined'
                  style={{ color: `${error ? COLORS.Red : COLORS.Green}` }}
                  id="taxLibraryToastIcon">
                  {error ? 'block' : 'check_circle'}
                </span>
                <span className='status-message' id="taxLibraryToastMessage">
                  {error
                    ? error?.body?.error?.message || error?.body?.error?.validationErrors[0].message
                    : message}
                </span>
              </div>
            }
            loading={loading}
          />
        )}
      </LibraryContainer>
    </div>
  );
};

export default TaxLibrary;
